import {
  Banner,
  Box,
  Divider,
  DropdownButtonContext,
  InputText,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ampli } from 'lib-shared-tracking';
import { Channel } from 'lib-shared-types-channels';
import noop from 'lodash/noop';
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

import { aiApi, ReadAiWritingImprovementArg } from 'apis/ai';
import { AiLoadingState } from 'components/aiLoadingState';
import {
  CopilotDropdownActionContext,
  useFindStyleGuideForChannel,
} from 'components/copilotDropdown';
import { CopilotDropdownFooter } from 'components/copilotDropdown/components';
import { HtmlRenderer } from 'components/htmlRenderer';
import { copilotUtils } from 'utils/copilot';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';

import { CopilotDropdownHeader } from '../../../copilotDropdown/components/header';
import { EditorCopilotDropdownContentBox } from '../contentBox';

import {
  type DisabledImprovement,
  ReplyImprovementsContainer,
} from './components/replyImprovementsContainer';
import { builtInImprovements } from './editorCopilotDropdownImproveReply.constants';

type EditorCopilotDropdownImproveReplyProps = {
  lastMessage: string;
  channel?: Channel;
  hasStyleGuideForChannel?: boolean;
  /** Allow consumers to define their custom improvements */
  disabledImprovements?: DisabledImprovement[];
  /** The CSID */
  csid?: string;
};

/** Component that represents the improve reply action in the copilot dropdown. */
export const EditorCopilotDropdownImproveReply = memo(
  function EditorCopilotDropdownImproveReply({
    channel,
    lastMessage,
    disabledImprovements = [],
    csid,
  }: EditorCopilotDropdownImproveReplyProps) {
    const {
      onBack,
      actionsComputed,
      latestUndoStackItem,
      pushDropdownUndoStack,
      popDropdownUndoStack,
    } = useContext(CopilotDropdownActionContext);

    const channelStyleGuideMatch = useFindStyleGuideForChannel();
    const actionComputed = actionsComputed.improveReply;
    const dropdownContext = useContext(DropdownButtonContext);
    const { getSelectedText, insertBelow, replace } = useEditorContext();
    const [customPrompt, setCustomPrompt] = useState('');
    const [lastUsedPrompt, setLastUsedPrompt] = useState<string | null>(null);
    const latestFeedbackId = latestUndoStackItem?.feedbackId || null;
    const latestAdditionalFeedback =
      latestUndoStackItem?.additionalFeedback || {};
    const currentEditorText = getSelectedText();

    useMount(() => {
      if (latestUndoStackItem === undefined) {
        pushDropdownUndoStack(currentEditorText);
      }
    });

    const [improveReply, { data }] =
      aiApi.useLazyReadStreamAiWritingImprovementQuery();
    const isStreaming = data?.meta?.isStreaming;
    const isStreamLoading = data?.meta?.isLoading;
    const isStreamError = data?.meta?.isError;
    const isStreamSuccess = data?.meta?.isSuccess;

    const latestContent = useMemo(() => {
      if (isStreaming) return data.text;
      if (latestUndoStackItem?.content) return latestUndoStackItem?.content;
      return '';
    }, [data?.text, isStreaming, latestUndoStackItem?.content]);
    const { isDisabled: disableImprovements, isInsufficientText } =
      copilotUtils.improveReply.getStatus({
        text: latestContent,
        isLoading: isStreaming || isStreamLoading,
      });

    const disableInsert = useMemo(() => {
      if (isStreaming || isStreamLoading) return true;
      return isInsufficientText || currentEditorText === latestContent;
    }, [
      isStreaming,
      isStreamLoading,
      isInsufficientText,
      currentEditorText,
      latestContent,
    ]);
    const disableTextModification = useMemo(() => {
      if (disableImprovements || isStreaming || isStreamLoading) return true;
    }, [disableImprovements, isStreamLoading, isStreaming]);

    const onSelect = useCallback(
      async (value) => {
        try {
          ampli.agentCopilotImproveWritingImprovementClicked({
            improvement: value,
            ...(csid ? { csid } : {}),
          });
          const includeContext = builtInImprovements.find(
            (i) => i.value === value,
          )?.includeContext;

          const improveReplyContext: ReadAiWritingImprovementArg['contexts'] = {
            features: 'impr_reply',
            ...(channel && {
              channels: convertChannelCasing(channel, 'snake') as Channel,
            }),
          };

          setLastUsedPrompt(value);

          improveReply({
            lastMessage,
            text: latestContent,
            promptName: value,
            ...(includeContext ? { contexts: improveReplyContext } : {}),
            ...(csid ? { csid } : {}),
          });
        } catch (error) {
          console.error(error);
        }
      },
      [channel, improveReply, lastMessage, latestContent, csid],
    );

    const handleCustomPromptSubmit = useCallback(async () => {
      try {
        ampli.agentCopilotImproveWritingImprovementClicked({
          improvement: 'custom_writing_improvement',
          ...(csid ? { csid } : {}),
        });
        setLastUsedPrompt('custom_writing_improvement');
        improveReply({
          lastMessage,
          text: latestContent,
          promptName: 'custom_writing_improvement',
          instructions: customPrompt,
          ...(csid ? { csid } : {}),
        });
      } catch (error) {
        console.error(error);
      }
    }, [customPrompt, lastMessage, latestContent, improveReply, csid]);

    useUpdateEffect(() => {
      if (isStreamSuccess) {
        pushDropdownUndoStack(data.text, data.feedback_id, {
          feature: 'improve_reply',
          improvementUsed: lastUsedPrompt,
        });
      }
    }, [isStreamSuccess]);

    const handleInsertBelow = useCallback(() => {
      dropdownContext.closeDropdown();

      insertBelow(latestContent);

      ampli.agentCopilotTextInsertBelowClicked({
        key: 'improveReply',
        ...(csid ? { csid } : {}),
      });
    }, [dropdownContext, insertBelow, latestContent]);

    const handleReplace = useCallback(() => {
      dropdownContext.closeDropdown();

      replace(latestContent);

      ampli.agentCopilotTextReplaceClicked({
        key: 'improveReply',
        ...(csid ? { csid } : {}),
      });
    }, [dropdownContext, latestContent, replace]);

    const showUndo = latestUndoStackItem !== undefined && !disableInsert;
    const handleUndo = useCallback(() => {
      popDropdownUndoStack();
    }, [popDropdownUndoStack]);

    const content = useMemo(() => {
      if (isStreamLoading) {
        return <AiLoadingState label="" />;
      }

      if (isStreamError) {
        return (
          <Banner
            variant="fail"
            description={
              <span>An error occurred while trying to improve the reply.</span>
            }
          />
        );
      }

      if (isInsufficientText && !isStreaming) {
        return (
          <Banner
            variant="warning"
            description={<span>There is not enough text to improve.</span>}
          />
        );
      }

      return (
        <HtmlRenderer
          sanitizationProfileType={sanitizationProfile}
          html={latestContent}
        />
      );
    }, [
      isStreamLoading,
      isStreamError,
      isInsufficientText,
      isStreaming,
      latestContent,
    ]);

    const getDisabledImprovements = useCallback(() => {
      if (disableImprovements) {
        return builtInImprovements;
      }

      if (!channelStyleGuideMatch) {
        const styleGuideDisabled = {
          value: 'repeat-input',
          tooltip: 'No style guide created for this channel',
        };

        return disabledImprovements
          ? [...disabledImprovements, styleGuideDisabled]
          : [styleGuideDisabled];
      }

      if (disabledImprovements) {
        return disabledImprovements;
      }

      return [];
    }, [disableImprovements, channelStyleGuideMatch, disabledImprovements]);

    return (
      <Box style={{ maxWidth: 768, minWidth: 540 }}>
        <CopilotDropdownHeader
          title={actionComputed.title}
          onBack={onBack}
          hideDivider
          feedbackId={latestFeedbackId}
          additionalFeedback={latestAdditionalFeedback}
        >
          <InputText
            placeholder="Tell AI how to edit it. E.g. Sound casual"
            suffix={{
              type: 'button',
              button: {
                icon: 'enter',
                onClick: disableTextModification
                  ? noop
                  : handleCustomPromptSubmit,
                disabled: disableTextModification,
              },
            }}
            onChange={(e) => {
              setCustomPrompt(e.target.value);
            }}
            onKeyPress={(e) => {
              if (disableTextModification) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCustomPromptSubmit();
              }
            }}
            disabled={disableTextModification}
          />
        </CopilotDropdownHeader>

        <ReplyImprovementsContainer
          onSelect={onSelect}
          improvements={builtInImprovements}
          disabled={disableTextModification}
          disabledImprovements={
            getDisabledImprovements() as DisabledImprovement[]
          }
        />

        <Divider />

        <EditorCopilotDropdownContentBox
          isEmphasized
          handleUndo={showUndo ? handleUndo : undefined}
        >
          {content}
        </EditorCopilotDropdownContentBox>

        <Divider />

        <CopilotDropdownFooter
          handleInsertBelow={handleInsertBelow}
          disableInsertBelow={disableInsert}
          handleReplace={handleReplace}
          disableReplace={disableInsert}
          hideImproveReply
        />
      </Box>
    );
  },
);
